import type { Me } from '@pebblecode/shared';

import { api, newOwnerToken } from '../api/client.js';
import { uiLangStore } from './prefs.js';
import { createStore, storage, useStore } from './store.js';

/**
 * Who is logged in (ADR 0014). `undefined` while the first `GET /auth/me` is in flight, `null`
 * for an anonymous browser. Logging in or out reloads the app, so every other store (projects,
 * rewards, the live connection) starts clean for the new identity.
 */
export const authStore = createStore<Me | null | undefined>(undefined);

export function useMe(): Me | null | undefined {
  return useStore(authStore);
}

const CLAIMED_KEY = 'pc.claimed';

export async function loadAuth(): Promise<void> {
  try {
    const { user } = await api.me();
    authStore.set(user);
  } catch {
    authStore.set(null);
  }
}

/** Refresh after an account change (name, teacher request, verification). */
export async function refreshAuth(): Promise<void> {
  await loadAuth();
}

/**
 * After login or sign-up: remember what moved into the account (the home page says so once),
 * adopt the account's UI language, and start the app over as the new identity.
 */
export function enterApp(
  me: Me,
  claimed: { projects: number; points: number } | undefined,
  to = '/',
): void {
  if (claimed && (claimed.projects > 0 || claimed.points > 0)) {
    storage.set(CLAIMED_KEY, JSON.stringify(claimed));
  }
  uiLangStore.set(me.uiLang);
  window.location.assign(to);
}

/** What the last login moved into the account; read once. */
export function takeClaimed(): { projects: number; points: number } | null {
  const raw = storage.get(CLAIMED_KEY);
  if (!raw) return null;
  storage.remove(CLAIMED_KEY);
  try {
    return JSON.parse(raw) as { projects: number; points: number };
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.logout();
  } finally {
    // The old anonymous token's work now belongs to the account; start a fresh one.
    newOwnerToken();
    window.location.assign('/');
  }
}

export const isTeacher = (me: Me | null | undefined) =>
  !!me && (me.role === 'teacher' || me.role === 'admin');
